<!-- @format -->

<template>
    <div class="graphical-email-templates-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ steps[step].title }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="template-source" v-if="clientId">
                <label>Source</label>
                <source-dropdown v-model="source" :clientId="clientId" />
                <app-input
                    class="search"
                    :value="search"
                    :placeholder="'Search'"
                    @input="updateSearch"
                >
                    <div slot="inputIcon" class="input-icon">
                        <magnifying-glass-svg />
                    </div>
                </app-input>
            </div>
            <div
                class="content-wrapper graphical-templates"
                v-infinite-scroll="getMoreGraphicalTemplates"
                infinite-scroll-disabled="loading"
                infinite-scroll-distance="50"
                :class="{ 'client-view': clientId }"
            >
                <div
                    :class="[{ disabled: template.isDisabled }, 'template']"
                    v-for="template in templates"
                    @mouseover="showTemplateActions(template)"
                    @mouseleave="hideTemplateActions(template)"
                    :key="template.id"
                >
                    <div
                        class="template-preview"
                        :style="{ 'background-image': `url('${template.previewImageUrl}')` }"
                    ></div>
                    <div class="template-name">
                        <p>{{ template.name }}</p>
                    </div>
                    <div class="template-actions" v-if="template.areActionsShown">
                        <app-button @click.native.stop="selectTemplate(template)">
                            <span class="fa fa-check"></span>Add this Email
                        </app-button>
                        <app-button @click.native.stop="openTemplatePreview(template)">
                            Preview
                        </app-button>
                    </div>
                    <div
                        :class="[
                            'template-selected',
                            {
                                'selected-shown': template.isSelectedShown,
                                'cancel-shown': template.isCancelSelectionShown,
                            },
                        ]"
                        v-if="template.isSelected"
                        @click.stop="unselectTemplate(template)"
                        @mouseover="showTemplateUnselect(template)"
                        @mouseleave="hideTemplateUnselect(template)"
                    >
                        <div class="selected-container">
                            <transition name="fade">
                                <large-circle-checkmark v-if="template.isSelectedShown" />
                                <div class="cancel" v-else-if="template.isCancelSelectionShown">
                                    <large-circle-x />
                                    <p class="undo-selection">Undo Selection</p>
                                </div>
                            </transition>
                        </div>
                    </div>
                </div>
                <loading-screen-svg v-if="loading" />
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="transparent-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div class="action-container">
                    <app-button class="transparent-btn" @click="showExitPagePopup">
                        Create New
                    </app-button>
                    <app-button
                        @click="steps[step].action"
                        :loading="steps[step].loading"
                        :disabled="isButtonDisabled || steps[step].disabled"
                    >
                        {{ steps[step].buttonText }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppInput = () => import('@/components/common/app-input');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg.vue');
const MagnifyingGlassSvg = () => import('@/components/svg/magnifying-glass-svg');
const LargeCircleCheckmark = () => import('@/components/svg/large-circle-checkmark.vue');
const LargeCircleX = () => import('@/components/svg/large-circle-x.vue');
const SourceDropdown = () => import('@/components/common/source-dropdown');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');

import AppPopup from '@/components/popups/app-popup';
import infiniteScroll from 'vue-infinite-scroll';
import { debounce } from 'lodash';
import EventBus from '@/scripts/event-bus.js';

export default {
    name: 'graphical-email-templates-widget',

    components: {
        'app-input': AppInput,
        'app-button': AppButton,
        'loading-screen-svg': LoadingScreenSvg,
        'magnifying-glass-svg': MagnifyingGlassSvg,
        'large-circle-checkmark': LargeCircleCheckmark,
        'large-circle-x': LargeCircleX,
        'source-dropdown': SourceDropdown,
        'widget-close-svg': WidgetCloseSvg,
    },

    directives: {
        infiniteScroll,
    },

    props: {
        options: {
            type: Object,
        },

        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            steps: {
                templates: {
                    title: 'Graphical Email Template',
                    buttonText: 'Add',
                    action: this.addGraphicalTemplate,
                    loading: false,
                },

                adding: {
                    title: 'Adding Template',
                    buttonText: 'Adding',
                    action: null,
                    loading: true,
                    disabled: true,
                },
            },

            top: 18,
            skip: 0,
            source: null,
            templatesCount: 0,
            loading: true,
            search: null,

            step: 'templates',

            templates: [],
            template: null,

            agencyId: this.widget.agencyId,
            clientId: this.widget.clientId,
            promoId: null,
        };
    },

    computed: {
        isButtonDisabled() {
            return this.template ? false : true;
        },
    },

    watch: {
        source(newValue, oldValue) {
            if (newValue != oldValue) {
                this.templates = [];
                this.skip = 0;
                this.template = null;
                this.getGraphicalTemplates();
            }
        },

        search() {
            this.templates = [];
            this.skip = 0;
            this.template = null;
            this.getGraphicalTemplates();
        },
    },

    created() {
        if (!this.clientId) {
            this.getGraphicalTemplates();
        } else {
            this.getPromoId();
        }
    },

    methods: {
        updateSearch: debounce(function (search) {
            this.search = search;
        }, 500),

        getSkipParam() {
            return `$skip=${this.skip}`;
        },

        getTopParam() {
            return `$top=${this.top}`;
        },

        addGraphicalTemplate() {
            this.step = 'adding';
            EventBus.$emit('template:select', this.template);
            this.closeWidget();
        },

        showTemplateActions(template) {
            if (!template.isDisabled && !template.isSelected) {
                template.areActionsShown = true;
            }
        },

        hideTemplateActions(template) {
            template.areActionsShown = false;
        },

        showTemplateUnselect(template) {
            template.isSelectedShown = false;
            template.isCancelSelectionShown = true;
        },

        hideTemplateUnselect(template) {
            template.isSelectedShown = true;
            template.isCancelSelectionShown = false;
        },

        selectTemplate(template) {
            template.isSelected = true;
            template.isSelectedShown = true;
            template.areActionsShown = false;
            this.template = template;

            this.templates.forEach((template) => {
                if (template.id != this.template.id) {
                    template.isDisabled = true;
                }
            });
        },

        unselectTemplate(template) {
            template.isSelected = false;
            template.isSelectedShown = false;
            template.isCancelSelectionShown = false;
            this.template = null;

            this.templates.forEach((template) => {
                template.isDisabled = false;
            });
        },

        removeTemplate() {
            this.template = null;
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        getGraphicalTemplates() {
            if (this.source && this.source.id > 0) {
                this.getSharedTemplates();
            } else if (this.source && this.source.name === 'Included With Subscription') {
                this.getSubscriptionTemplates();
            } else {
                this.getMyTemplates();
            }
        },

        getSubscriptionTemplatesQueryParams() {
            var filters = [`TemplateId ne null`, `PromoId eq ${this.promoId}`];

            if (this.search) {
                var encodedSearch = encodeURIComponent(this.search).replace("'", "''");
                filters.push(`contains(Template/Name, '${encodedSearch}')`);
            }

            var filterParam = `$filter=${filters.join(' and ')}`;

            var orderParam = `$orderby=Template/CreatedAt desc`;
            var selectParam = `$select=Template`;
            var expandParam = `$expand=Template($select=Id,Name,PreviewImageUrl,Configuration,ClientId)`;

            return [
                filterParam,
                orderParam,
                selectParam,
                expandParam,
                this.getTopParam(),
                this.getSkipParam(),
                `$count=true`,
            ].join('&');
        },

        getSubscriptionTemplates() {
            this.loading = true;

            var query = `/service/SharedAssets?${this.getSubscriptionTemplatesQueryParams()}`;

            this.$api
                .get(query)
                .then((response) => {
                    this.templatesCount = response.data['@odata.count'];

                    var templates = response.data.value.map((sharedAsset) => {
                        var previewImageUrl = sharedAsset.Template.PreviewImageUrl;

                        return {
                            id: sharedAsset.Template.Id,
                            name: sharedAsset.Template.Name,
                            clientId: sharedAsset.Template.ClientId,
                            previewImageUrl: previewImageUrl,
                            configuration: sharedAsset.Template.Configuration,
                            areActionsShown: false,
                            isSelected: false,
                            isSelectedShown: false,
                            isCancelSelectionShown: false,
                            isDisabled: this.template ? true : false,
                        };
                    });

                    this.templates = [...this.templates, ...templates];
                    this.skip += 18;
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while getting template info.',
                        { error },
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        getSharedTemplateQueryParams() {
            var filters = [
                `TemplateId ne null`,
                `AgencyClient/AgencyId eq ${this.source.id}`,
                `AgencyClient/ClientId eq ${this.clientId}`,
            ];

            if (this.search) {
                var encodedSearch = encodeURIComponent(this.search).replace("'", "''");
                filters.push(`contains(Template/Name, '${encodedSearch}')`);
            }

            var filterParam = `$filter=${filters.join(' and ')}`;
            var orderParam = `$orderby=Template/CreatedAt desc`;
            var selectParam = `$select=Template`;
            var expandParam = `$expand=Template($select=Id,Name,PreviewImageUrl,Configuration)`;

            return [
                filterParam,
                orderParam,
                selectParam,
                expandParam,
                this.getTopParam(),
                this.getSkipParam(),
                `$count=true`,
            ].join('&');
        },

        getSharedTemplates() {
            this.loading = true;

            var query = `/service/SharedAssets?${this.getSharedTemplateQueryParams()}`;

            this.$api
                .get(query)
                .then((response) => {
                    this.templatesCount = response.data['@odata.count'];

                    var templates = response.data.value.map((sharedAsset) => {
                        var previewImageUrl = sharedAsset.Template.PreviewImageUrl;

                        return {
                            id: sharedAsset.Template.Id,
                            name: sharedAsset.Template.Name,
                            clientId: null,
                            previewImageUrl: previewImageUrl,
                            configuration: sharedAsset.Template.Configuration,
                            areActionsShown: false,
                            isSelected: false,
                            isSelectedShown: false,
                            isCancelSelectionShown: false,
                            isDisabled: this.template ? true : false,
                        };
                    });

                    this.templates = [...this.templates, ...templates];
                    this.skip += 18;
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while getting template info.',
                        { error },
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        getMyTemplateQueryParams() {
            var filters = [];

            if (this.search) {
                var encodedSearch = encodeURIComponent(this.search).replace("'", "''");
                filters.push(`contains(Name, '${encodedSearch}')`);
            }

            if (this.clientId) {
                filters.push(`ClientId eq ${this.clientId}`);
            } else if (this.agencyId) {
                filters.push(`AgencyId eq ${this.agencyId}`);
            } else {
                filters.push(`AgencyId eq null and ClientId eq null`);
            }

            if (filters.length > 0) {
                var filterParam = `$filter=${filters.join(' and ')}`;
            } else {
                filterParam = '';
            }

            var orderParam = `$orderby=CreatedAt desc`;
            var selectParam = `$select=Id,Name,PreviewImageUrl,Configuration,ClientId`;

            return [
                filterParam,
                orderParam,
                selectParam,
                this.getTopParam(),
                this.getSkipParam(),
                `$count=true`,
            ].join('&');
        },

        getMyTemplates() {
            this.loading = true;

            var query = `/service/Templates?${this.getMyTemplateQueryParams()}`;

            this.$api
                .get(query)
                .then((response) => {
                    this.templatesCount = response.data['@odata.count'];
                    var templates = response.data.value.map((template) => {
                        return {
                            id: template.Id,
                            name: template.Name,
                            clientId: template.ClientId,
                            previewImageUrl: template.PreviewImageUrl,
                            configuration: template.Configuration,
                            areActionsShown: false,
                            isSelected: false,
                            isSelectedShown: false,
                            isCancelSelectionShown: false,
                            isDisabled: this.template ? true : false,
                        };
                    });

                    this.templates = [...this.templates, ...templates];
                    this.skip += 18;
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while getting your templates.',
                        { error },
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        getMoreGraphicalTemplates() {
            if (this.templates.length < this.templatesCount) {
                this.getGraphicalTemplates();
            }
        },

        showExitPagePopup() {
            var actions = {
                confirm: {
                    label: 'Leave',
                    type: 'leave',
                    action: this.goToTemplateCreate,
                },

                cancel: {
                    label: 'Cancel',
                },
            };

            this.$modal.show(
                AppPopup,
                {
                    type: 'alert-popup',
                    title: 'Warning - Leaving Page',
                    content: `<p>You are about to leave this page to create a new graphical email template. You are about to lose any changes to the email message you are currently working on.</p><br /><strong>Are you sure you want to leave?</strong>`,
                    actions: actions,
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                },
            );
        },

        goToTemplateCreate() {
            EventBus.$emit('close-widgets');
            if (this.clientId) {
                this.$router.push(`/Clients/${this.clientId}/Templates/Create`);
            } else if (this.agencyId) {
                this.$router.push(`/Agencies/${this.agencyId}/Templates/Create`);
            }
        },

        openTemplatePreview(template) {
            if (this.clientId) {
                window.open(`/Clients/${this.clientId}/Templates/${template.id}/preview`, '_blank');
            } else if (this.agencyId) {
                window.open(
                    `/Agencies/${this.agencyId}/Templates/${template.id}/preview`,
                    '_blank',
                );
            }
        },

        getPromoId() {
            this.$api
                .get(`/service/Clients?$filter=Id eq ${this.clientId}&$select=PromoId`)
                .then((response) => {
                    this.promoId = response.data.value[0].PromoId;
                })
                .catch((error) => {
                    this.$helpers.showFailNotification(
                        'An error occurred while getting your templates.',
                        { error },
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>

<style>
.graphical-email-templates-widget.widget .widget-header {
    background: rgb(80, 82, 254);
    background: linear-gradient(90deg, rgba(80, 82, 254, 1) 0%, rgba(75, 201, 254, 1) 100%);
}

.graphical-email-templates-widget.widget .content-wrapper {
    padding-top: 10px;
    padding-right: 24px;
    padding-left: 24px;
    height: calc(100% - 152px);
}

.graphical-email-templates-widget.widget .client-view.content-wrapper {
    height: calc(100% - 287px);
}

.graphical-email-templates-widget .btn.transparent-btn {
    background: transparent;
    color: #877d88;
}

.graphical-email-templates-widget .btn.transparent-btn:hover {
    background: #e1e0e5;
}

.graphical-email-templates-widget .action-container .btn.transparent-btn {
    color: #18aeff;
}

.graphical-email-templates-widget .action-container .btn.transparent-btn:hover {
    background: rgba(24, 174, 255, 0.11);
}

.graphical-email-templates-widget .graphical-templates {
    display: flex;
    width: 100%;
    height: calc(100% - 152px);
    overflow: auto;
    padding: 45px 35px;
    flex-wrap: wrap;
    justify-content: flex-start;
}

.graphical-email-templates-widget .graphical-templates .loading-screen {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    margin-left: 226px;
}

.graphical-email-templates-widget .graphical-templates .template {
    max-width: 154px;
    max-height: 227px;
    margin: 0 auto;
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    border-radius: 13px;
    box-shadow: 0px 3px 6px rgba(130, 116, 232, 0.1);
    padding: 20px 16px;
    cursor: pointer;
    position: relative;
}

.graphical-email-templates-widget .template-source {
    padding: 10px 24px 10px 24px;
    width: 100%;
}

.graphical-email-templates-widget .graphical-templates .template .template-preview {
    height: 160px;
    width: 123px;
    background-size: cover;
    background-position: top left;
    margin-bottom: 13px;
}

.graphical-email-templates-widget .graphical-templates .template .template-name {
    white-space: nowrap;
    overflow: hidden;
    width: 100%;
    text-align: center;
}

.graphical-email-templates-widget .graphical-templates .template .template-name p {
    text-overflow: ellipsis;
    overflow: hidden;
    margin-bottom: 0;
    color: #382153;
    line-height: 14px;
    font-size: 12px;
    font-weight: 600;
    user-select: none;
}

.graphical-email-templates-widget .graphical-templates .template .template-actions,
.graphical-email-templates-widget .graphical-templates .template .template-selected {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
}

.graphical-email-templates-widget .graphical-templates .template .template-actions {
    background: rgba(89, 72, 110, 0.76);
}

.graphical-email-templates-widget .graphical-templates .template .template-actions .btn {
    background: transparent;
    border: 2px solid #ffffff;
    margin-bottom: 10px;
    width: 129px;
    height: 32px;
    padding: 0px;
}

.graphical-email-templates-widget .graphical-templates .template .template-actions .btn:hover {
    background: #ffffff;
    color: #18aeff;
}

.graphical-email-templates-widget .graphical-templates .template .template-actions .btn .fa-check {
    margin-right: 5px;
}

.graphical-email-templates-widget .graphical-templates .template.disabled {
    cursor: not-allowed;
    opacity: 0.5;
    background: #e9e9ef;
}

.graphical-email-templates-widget .graphical-templates .template .template-selected {
    transition: background 0.5s ease-out;
}

.graphical-email-templates-widget .graphical-templates .template .template-selected.selected-shown {
    background: rgba(24, 174, 255, 0.76);
}

.graphical-email-templates-widget .graphical-templates .template .template-selected.cancel-shown {
    background: rgba(89, 72, 110, 0.76);
}

.graphical-email-templates-widget
    .graphical-templates
    .template
    .template-selected
    .fade-enter-active,
.graphical-email-templates-widget
    .graphical-templates
    .template
    .template-selected
    .fade-leave-active {
    transition: all 0.5s ease-in-out;
}

.graphical-email-templates-widget .graphical-templates .template .template-selected .fade-enter,
.graphical-email-templates-widget .graphical-templates .template .template-selected .fade-leave-to {
    opacity: 0;
}

.graphical-email-templates-widget
    .graphical-templates
    .template
    .template-selected
    .selected-container
    .large-circle-checkmark,
.graphical-email-templates-widget
    .graphical-templates
    .template
    .template-selected
    .selected-container
    .cancel
    .large-circle-x {
    width: 73px;
    height: 73px;
}

.graphical-email-templates-widget
    .graphical-templates
    .template
    .template-selected
    .selected-container {
    position: relative;
}

.graphical-email-templates-widget
    .graphical-templates
    .template
    .template-selected
    .selected-container
    .large-circle-checkmark,
.graphical-email-templates-widget
    .graphical-templates
    .template
    .template-selected
    .selected-container
    .cancel {
    position: absolute;
    top: calc(50% - 36.5px);
    left: calc(50% - 36.5px);
}

.graphical-email-templates-widget
    .graphical-templates
    .template
    .template-selected
    .selected-container
    .cancel {
    top: calc(50% - 69px);
}

.graphical-email-templates-widget
    .graphical-templates
    .template
    .template-selected
    .selected-container
    .cancel
    .undo-selection {
    font-size: 18px;
    line-height: 25px;
    text-align: center;
    color: #ffffff;
    font-weight: 600;
}

.graphical-email-templates-widget .widget-footer .action-container {
    display: flex;
}

.graphical-email-templates-widget .widget-footer .action-container .btn {
    margin-left: 10px;
}

.graphical-email-templates-widget .search {
    margin-top: 10px;
}

.graphical-email-templates-widget .search input {
    position: relative;
}

.graphical-email-templates-widget .search .icon-container svg {
    width: 16px;
    height: 16px;
}

.graphical-email-templates-widget .search .input-icon .magnifying-glass-svg {
    margin-top: 5px;
    margin-left: 4px;
    width: 13px;
}

.graphical-email-templates-widget
    .app-input.search
    .input-container
    input
    ~ .icon-container
    .magnifying-glass-svg
    path {
    fill: #f30070;
}
</style>
